import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Popover, PopoverTrigger, PopoverContent,
} from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Wrench, Check, X, Plus, Loader2, ArrowLeft, Info } from 'lucide-react';
import { toast } from 'sonner';
import { computeRowPricing, DEFAULT_MARGIN_PCT } from '@/lib/pricing';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ALLOWED_ROLES = ['super_admin', 'admin'];

// Sum a template's line_items using the canonical invoice pricing so the
// mapping preview matches exactly what the customer is charged. A BLANK
// parts margin defaults to 40% (same as the backend invoice pricing) so
// the picker total, the mapping badge, and the final invoice all agree.
const withMarginDefault = (li) => {
  const wantsParts = (li.category || 'part_and_labour') === 'part'
    || (li.category || 'part_and_labour') === 'part_and_labour';
  if (wantsParts && (li.parts_margin_pct == null || li.parts_margin_pct === '')) {
    return { ...li, parts_margin_pct: DEFAULT_MARGIN_PCT };
  }
  return li;
};
const templateTotal = (t) =>
  (t.line_items || []).reduce((sum, li) => sum + (computeRowPricing(withMarginDefault(li)).line_total || 0), 0);
const templateDesc = (t) =>
  (t.line_items && t.line_items[0]?.description) || t.name || '';

export default function SuperAdminServiceMappings() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mappings, setMappings] = useState([]);
  const [invoiceTemplates, setInvoiceTemplates] = useState([]);
  const [savingKey, setSavingKey] = useState(null);
  const [openPickerFor, setOpenPickerFor] = useState(null); // service_type currently editing
  const [pickerFilter, setPickerFilter] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/auth/me`, { credentials: 'include' });
        if (!r.ok) return navigate('/login');
        const u = await r.json();
        if (!ALLOWED_ROLES.includes(u.role)) {
          toast.error('Super Admin or workshop Admin only');
          return navigate('/');
        }
        setUser(u);
      } catch { navigate('/login'); }
      finally { setLoading(false); }
    })();
  }, [navigate]);

  const loadMappings = useCallback(async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/service-template-mappings`, { credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setMappings(d.mappings || []);
    } catch (e) {
      toast.error(`Failed to load mappings — ${e.message}`);
    }
  }, []);

  const loadTemplates = useCallback(async () => {
    try {
      // iter188 — the mapping picker now lists the SM's REAL Invoice
      // Templates (category=invoice_template) — the multi-line ones built
      // on the Invoice Templates page & used in the Quote Builder.
      const r = await fetch(`${BACKEND_URL}/api/templates?category=invoice_template`, {
        credentials: 'include',
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      const list = Array.isArray(d) ? d : (d.templates || []);
      setInvoiceTemplates(list.filter((t) => !t.deleted_at));
    } catch (e) {
      console.error('Failed to load templates', e);
    }
  }, []);

  useEffect(() => {
    if (user) {
      loadMappings();
      loadTemplates();
    }
  }, [user, loadMappings, loadTemplates]);

  const saveMapping = async (serviceType, templateIds) => {
    setSavingKey(serviceType);
    try {
      const r = await fetch(`${BACKEND_URL}/api/service-template-mappings/${serviceType}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ template_ids: templateIds }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success('Mapping saved');
      await loadMappings();
    } catch (e) {
      toast.error(`Save failed — ${e.message}`);
    } finally {
      setSavingKey(null);
    }
  };

  const clearMapping = async (serviceType) => {
    setSavingKey(serviceType);
    try {
      const r = await fetch(`${BACKEND_URL}/api/service-template-mappings/${serviceType}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success('Mapping cleared');
      await loadMappings();
    } catch (e) {
      toast.error(`Clear failed — ${e.message}`);
    } finally {
      setSavingKey(null);
    }
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-4" data-testid="service-mappings-page">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/super-admin')}
            className="text-slate-500 hover:text-slate-900"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Wrench className="h-6 w-6 text-indigo-600" />
              Service → Invoice Template Mapping
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Map each service the customer picks (RWC, Service, Tyres, etc.) to one or more of your Invoice Templates.
              When a booking is created, the mapped template&apos;s line items (description + pricing + labour + supplier) are auto-added as JC tasks so the invoice at the end matches your standard rate.
            </p>
          </div>
        </div>

        <Card className="p-3 bg-blue-50/50 border-blue-200 flex gap-2 items-start">
          <Info className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-blue-900">
            <p><strong>How it works:</strong> When a customer selects &quot;RWC&quot; at the kiosk (or SM picks it in the booking modal), the job card is created with the mapped template&apos;s line items — pre-filled with quoted labour hours, parts price, supplier, and margin. No more zero-priced generic tasks.</p>
            <p className="mt-1"><strong>No mapping:</strong> falls back to a generic labelled task (safe default — mechanic still sees the customer&apos;s chosen services).</p>
          </div>
        </Card>

        <Card className="divide-y divide-slate-200">
          {mappings.map((m) => {
            const isSaving = savingKey === m.service_type;
            const isPickerOpen = openPickerFor === m.service_type;
            const availableTemplates = invoiceTemplates.filter((t) => {
              if (!pickerFilter) return true;
              const q = pickerFilter.toLowerCase();
              return (t.name || '').toLowerCase().includes(q)
                  || templateDesc(t).toLowerCase().includes(q);
            });

            return (
              <div key={m.service_type} className="p-3 flex items-start gap-3" data-testid={`mapping-row-${m.service_type}`}>
                <div className="w-40 flex-shrink-0">
                  <p className="text-sm font-semibold text-slate-900">{m.label}</p>
                  <p className="text-[11px] text-slate-500 font-mono">{m.service_type}</p>
                  {m.is_global_fallback && (
                    <Badge variant="outline" className="text-[10px] mt-0.5">global fallback</Badge>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  {m.templates.length === 0 ? (
                    <p className="text-xs text-slate-400 italic">
                      No mapping — uses generic label ({m.label}) with no default pricing.
                    </p>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {m.templates.map((t) => (
                        <Badge
                          key={t.template_id}
                          className="bg-indigo-100 text-indigo-800 hover:bg-indigo-200 gap-1 pr-1"
                          data-testid={`mapping-tpl-${m.service_type}-${t.template_id}`}
                        >
                          <span className="font-medium">{t.name}</span>
                          <span className="text-[10px] font-mono opacity-70">
                            ${(t.preview_total || 0).toFixed(0)}
                          </span>
                          <button
                            className="ml-1 hover:text-red-700"
                            onClick={() => {
                              const next = m.template_ids.filter((tid) => tid !== t.template_id);
                              saveMapping(m.service_type, next);
                            }}
                            title="Remove"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  <Popover open={isPickerOpen} onOpenChange={(o) => { setOpenPickerFor(o ? m.service_type : null); if (!o) setPickerFilter(''); }}>
                    <PopoverTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs"
                        disabled={isSaving}
                        data-testid={`mapping-add-btn-${m.service_type}`}
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Add template
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-96 p-2" align="end">
                      <Input
                        placeholder="Search templates..."
                        value={pickerFilter}
                        onChange={(e) => setPickerFilter(e.target.value)}
                        className="h-8 text-sm mb-2"
                        autoFocus
                      />
                      <div className="max-h-64 overflow-y-auto space-y-1">
                        {availableTemplates.length === 0 && (
                          <p className="text-xs text-slate-400 italic px-2 py-3 text-center">
                            {invoiceTemplates.length === 0
                              ? 'No invoice templates yet — create some on the Invoice Templates page first.'
                              : 'No matches.'}
                          </p>
                        )}
                        {availableTemplates.map((t) => {
                          const alreadyLinked = m.template_ids.includes(t.template_id);
                          const preview = templateTotal(t).toFixed(0);
                          const lineCount = (t.line_items || []).length;
                          return (
                            <button
                              key={t.template_id}
                              disabled={alreadyLinked}
                              onClick={() => {
                                saveMapping(m.service_type, [...m.template_ids, t.template_id]);
                                setOpenPickerFor(null);
                                setPickerFilter('');
                              }}
                              className={`w-full text-left px-2 py-1.5 rounded hover:bg-slate-100 flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed`}
                              data-testid={`mapping-picker-tpl-${t.template_id}`}
                            >
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-slate-900 truncate">{t.name}</p>
                                <p className="text-[11px] text-slate-500 truncate">
                                  {lineCount} line item{lineCount === 1 ? '' : 's'}
                                  {templateDesc(t) ? ` · ${templateDesc(t)}` : ''}
                                </p>
                              </div>
                              <span className="text-xs font-mono text-slate-600">${preview}</span>
                              {alreadyLinked && <Check className="h-4 w-4 text-emerald-600" />}
                            </button>
                          );
                        })}
                      </div>
                    </PopoverContent>
                  </Popover>

                  {m.templates.length > 0 && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                      disabled={isSaving}
                      onClick={() => clearMapping(m.service_type)}
                      data-testid={`mapping-clear-${m.service_type}`}
                    >
                      Clear
                    </Button>
                  )}
                  {isSaving && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
                </div>
              </div>
            );
          })}
        </Card>
      </div>
    </Layout>
  );
}
